import { API } from '@hefe/store';
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CrudApiResource,
  CrudCreateArgs,
  CrudDeleteArgs,
  CrudListArgs,
  CrudPathParams,
  CrudUpdateArgs,
} from '../models/crud.types';

const normalizePath = (value: string): string => {
  if (!value) {
    return '/';
  }

  return value.startsWith('/') ? value : `/${value}`;
};

const resolveEndpoint = (endpoint: string, params?: CrudPathParams): string => {
  return endpoint.replace(/:([a-zA-Z0-9_]+)/g, (_, key: string) => {
    const value = params?.[key];

    if (value === null || value === undefined) {
      throw new Error(`Missing path param: ${key}`);
    }

    return encodeURIComponent(String(value));
  });
};

const buildQueryString = (
  query?: Record<string, string | number | boolean | null | undefined>
): string => {
  if (!query) {
    return '';
  }

  const searchParams = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.append(key, String(value));
    }
  });

  const result = searchParams.toString();

  return result ? `?${result}` : '';
};

const resourceKey = (endpoint: string, params?: CrudPathParams): string => {
  return normalizePath(resolveEndpoint(endpoint, params)).replace(/^\//, '');
};

const resourceItemKey = (
  endpoint: string,
  params: CrudPathParams | undefined,
  id: string | number
): string => {
  return `${resourceKey(endpoint, params)}:${id}`;
};

const normalizeListResponse = (response: unknown): any[] => {
  if (Array.isArray(response)) {
    return response;
  }

  if (response && typeof response === 'object') {
    const objectResponse = response as Record<string, unknown>;

    if (Array.isArray(objectResponse['data'])) {
      return objectResponse['data'] as any[];
    }

    if (Array.isArray(objectResponse['items'])) {
      return objectResponse['items'] as any[];
    }
  }

  return [];
};

export const crudApi = createApi({
  reducerPath: 'apiCrud',
  baseQuery: API.baseQuery,
  tagTypes: ['CrudResource'],
  endpoints: (builder) => ({
    crudList: builder.query<any[], CrudListArgs<any>>({
      query: ({ resource, params, query }) => {
        const endpoint = resolveEndpoint(resource.endpoint, params);
        return {
          url: `${normalizePath(endpoint)}${buildQueryString(query)}`,
          method: 'GET',
        };
      },
      transformResponse: (response: unknown, meta, arg) => {
        if (arg.resource.transforms?.listResponse) {
          return arg.resource.transforms.listResponse(response);
        }

        return normalizeListResponse(response);
      },
      serializeQueryArgs: ({ queryArgs }) => {
        const endpoint = resolveEndpoint(
          queryArgs.resource.endpoint,
          queryArgs.params
        );
        return `${normalizePath(endpoint)}${buildQueryString(queryArgs.query)}`;
      },
      providesTags: (result, error, arg) => {
        const listKey = resourceKey(arg.resource.endpoint, arg.params);
        const items = Array.isArray(result) ? result : [];

        if (!items.length) {
          return [{ type: 'CrudResource' as const, id: listKey }];
        }

        return [
          ...items.map((item: any) => ({
            type: 'CrudResource' as const,
            id: resourceItemKey(
              arg.resource.endpoint,
              arg.params,
              item[arg.resource.rowKey as string]
            ),
          })),
          { type: 'CrudResource' as const, id: listKey },
        ];
      },
    }),
    crudGet: builder.query<
      unknown,
      {
        resource: CrudApiResource<any>;
        params?: CrudPathParams;
        id: string | number;
      }
    >({
      query: ({ resource, params, id }) => {
        const endpoint = resolveEndpoint(resource.endpoint, params);
        return {
          url: `${normalizePath(endpoint)}/${id}`,
          method: 'GET',
        };
      },
      transformResponse: (response: unknown, meta, arg) => {
        if (arg.resource.transforms?.getResponse) {
          return arg.resource.transforms.getResponse(response);
        }

        return response;
      },
      providesTags: (result, error, arg) => [
        {
          type: 'CrudResource' as const,
          id: resourceItemKey(arg.resource.endpoint, arg.params, arg.id),
        },
      ],
    }),
    crudCreate: builder.mutation<unknown, CrudCreateArgs<any>>({
      query: ({ resource, params, data }) => {
        const endpoint = resolveEndpoint(resource.endpoint, params);
        const payload = resource.transforms?.createPayload
          ? resource.transforms.createPayload(data)
          : data;

        return {
          url: normalizePath(endpoint),
          method: 'POST',
          body: payload,
        };
      },
      transformResponse: (response: unknown, meta, arg) => {
        if (arg.resource.transforms?.createResponse) {
          return arg.resource.transforms.createResponse(response);
        }

        return response;
      },
      invalidatesTags: (result, error, arg) => [
        {
          type: 'CrudResource' as const,
          id: resourceKey(arg.resource.endpoint, arg.params),
        },
      ],
    }),
    crudUpdate: builder.mutation<unknown, CrudUpdateArgs<any>>({
      query: ({ resource, params, id, data }) => {
        const endpoint = resolveEndpoint(resource.endpoint, params);
        const payload = resource.transforms?.updatePayload
          ? resource.transforms.updatePayload(data)
          : data;

        return {
          url: `${normalizePath(endpoint)}/${id}`,
          method: 'PATCH',
          body: payload,
        };
      },
      transformResponse: (response: unknown, meta, arg) => {
        if (arg.resource.transforms?.updateResponse) {
          return arg.resource.transforms.updateResponse(response);
        }

        return response;
      },
      invalidatesTags: (result, error, arg) => [
        {
          type: 'CrudResource' as const,
          id: resourceKey(arg.resource.endpoint, arg.params),
        },
        {
          type: 'CrudResource' as const,
          id: resourceItemKey(arg.resource.endpoint, arg.params, arg.id),
        },
      ],
    }),
    crudDelete: builder.mutation<void, CrudDeleteArgs<any>>({
      query: ({ resource, params, id }) => {
        const endpoint = resolveEndpoint(resource.endpoint, params);

        return {
          url: `${normalizePath(endpoint)}/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [
        {
          type: 'CrudResource' as const,
          id: resourceKey(arg.resource.endpoint, arg.params),
        },
        {
          type: 'CrudResource' as const,
          id: resourceItemKey(arg.resource.endpoint, arg.params, arg.id),
        },
      ],
    }),
  }),
});

export const {
  useCrudListQuery,
  useLazyCrudGetQuery,
  useCrudCreateMutation,
  useCrudUpdateMutation,
  useCrudDeleteMutation,
  reducerPath,
  reducer,
  middleware,
} = crudApi;
